<template>
  <div class="brand-rankings-heatmap">
    <v-card variant="outlined">
      <!-- Persona Header -->
      <v-card-title class="persona-header">
        <v-icon class="mr-2" color="primary">mdi-account</v-icon>
        <span>{{ personaName }}</span>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Query rows -->
        <div 
          v-for="(query, index) in queries" 
          :key="query.id"
          :class="['query-row', { 'border-top': index > 0 }]"
        >
          <!-- Left: Query text -->
          <div class="query-section">
            <v-icon size="18" color="grey" class="query-icon">mdi-magnify</v-icon>
            <span class="query-text">{{ query.query_text }}</span>
          </div>

          <!-- Right: Primary brand status + competitors -->
          <div class="status-section">
            <!-- Primary Brand Status Card -->
            <div :class="['brand-status-card', getPrimaryBrandStatusClass(query)]">
              <div class="rank-display">
                <span class="rank-number">{{ getPrimaryBrandRank(query) }}</span>
                <span class="rank-context">{{ getRankContext(query) }}</span>
              </div>
              <div class="status-label">{{ getPrimaryBrandStatusLabel(query) }}</div>
            </div>

            <!-- Competitors summary (expandable) -->
            <div class="competitors-section">
              <button 
                class="competitors-toggle"
                @click="toggleExpanded(query.id)"
              >
                <span
                  class="toggle-text"
                  :class="{ 'brand-paywall-blur': isFreeTierPlan && competitorsSummaryHasName(query) }"
                >
                  {{ getCompetitorsSummary(query) }}
                </span>
                <v-icon size="16">
                  {{ expandedQueries[query.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </button>
              
              <!-- Expanded competitors list -->
              <div v-if="expandedQueries[query.id]" class="competitors-list">
                <div 
                  v-for="ranking in getAllRankings(query)" 
                  :key="`${query.id}-${ranking.brand_name}`"
                  :class="['competitor-item', ranking.primary ? getRankingHighlightClass(query) : '']"
                >
                  <span :class="['competitor-rank', ranking.primary ? 'primary-rank' : '']">#{{ ranking.position }}</span>
                  <span
                    :class="[
                      'competitor-name',
                      ranking.primary ? 'primary-name' : '',
                      { 'brand-paywall-blur': isFreeTierPlan && !ranking.primary }
                    ]"
                  >{{ ranking.brand_name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'BrandRankingsHeatmap',
  props: {
    queries: {
      type: Array,
      default: () => []
    },
    personaName: {
      type: String,
      required: true
    },
    primaryBrandName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      expandedQueries: {}
    }
  },
  computed: {
    ...mapGetters(['isFreeTierPlan']),
    primaryBrandFromRankings() {
      // Find the primary brand from any query's rankings
      for (const query of this.queries) {
        const rankings = query.scores?.ai_rankings || []
        const primaryRanking = rankings.find(r => r.primary === true)
        if (primaryRanking) {
          return primaryRanking.brand_name
        }
      }
      // Fallback to prop if not found in rankings
      return this.primaryBrandName
    },
  },
  methods: {
    toggleExpanded(queryId) {
      this.expandedQueries = {
        ...this.expandedQueries,
        [queryId]: !this.expandedQueries[queryId]
      }
    },
    getPrimaryBrandRanking(query) {
      const rankings = query.scores?.ai_rankings || []
      return rankings.find(r => r.primary === true)
    },
    getPrimaryBrandRank(query) {
      const ranking = this.getPrimaryBrandRanking(query)
      if (!ranking) return '—'
      return ranking.position ? `#${ranking.position}` : '—'
    },
    getRankContext(query) {
      const rankings = query.scores?.ai_rankings || []
      const totalBrands = rankings.length
      const ranking = this.getPrimaryBrandRanking(query)
      
      if (!ranking || !ranking.position) {
        return `of ${totalBrands} brands`
      }
      return `of ${totalBrands}`
    },
    getPrimaryBrandStatusClass(query) {
      const ranking = this.getPrimaryBrandRanking(query)
      
      if (!ranking || !ranking.position) {
        return 'status-not-mentioned'
      }
      
      const rank = ranking.position
      if (rank <= 3) {
        return 'status-excellent'
      } else if (rank <= 6) {
        return 'status-good'
      } else {
        return 'status-poor'
      }
    },
    getPrimaryBrandStatusLabel(query) {
      const ranking = this.getPrimaryBrandRanking(query)
      
      if (!ranking || !ranking.position) {
        return 'Not Mentioned'
      }
      
      const rank = ranking.position
      if (rank === 1) {
        return 'Top Recommendation'
      } else if (rank <= 3) {
        return 'Top 3'
      } else if (rank <= 6) {
        return 'Mentioned'
      } else {
        return 'Low Visibility'
      }
    },
    getCompetitorsSummary(query) {
      const rankings = query.scores?.ai_rankings || []
      const competitors = rankings.filter(r => !r.primary)
      const topCompetitor = competitors.find(r => r.position === 1)

      if (topCompetitor) {
        return `#1: ${topCompetitor.brand_name}`
      } else if (competitors.length > 0) {
        return `${competitors.length} competitor${competitors.length > 1 ? 's' : ''}`
      }
      return 'No competitors'
    },
    // True when the collapsed summary would reveal a competitor's name (the "#1: <name>" form).
    competitorsSummaryHasName(query) {
      const rankings = query.scores?.ai_rankings || []
      return rankings.some(r => !r.primary && r.position === 1)
    },
    getCompetitorRankings(query) {
      const rankings = query.scores?.ai_rankings || []
      return rankings
        .filter(r => !r.primary)
        .sort((a, b) => (a.position || 999) - (b.position || 999))
    },
    getAllRankings(query) {
      const rankings = query.scores?.ai_rankings || []
      return rankings
        .slice()
        .sort((a, b) => (a.position || 999) - (b.position || 999))
    },
    getRankingHighlightClass(query) {
      const ranking = this.getPrimaryBrandRanking(query)
      
      if (!ranking || !ranking.position) {
        return 'highlight-not-mentioned'
      }
      
      const rank = ranking.position
      if (rank <= 3) {
        return 'highlight-excellent'
      } else if (rank <= 6) {
        return 'highlight-good'
      } else {
        return 'highlight-poor'
      }
    }
  }
}
</script>

<style scoped>
.brand-rankings-heatmap {
  width: 100%;
}

.persona-header {
  font-size: 16px;
  font-weight: 600;
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.query-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px;
  gap: 16px;
}

.query-row.border-top {
  border-top: 1px solid #e0e0e0;
}

.query-row:hover {
  background-color: #fafafa;
}

/* Left side - Query */
.query-section {
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.query-icon {
  flex-shrink: 0;
  margin-right: 8px;
  margin-top: 2px;
}

.query-text {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  line-height: 1.5;
}

/* Right side - Status */
.status-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-shrink: 0;
}

/* Primary Brand Status Card */
.brand-status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  min-width: 90px;
  text-align: center;
}

.rank-display {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.rank-number {
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
}

.rank-context {
  font-size: 11px;
  opacity: 0.8;
}

.status-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

/* Status colors */
.brand-status-card.status-excellent {
  background-color: #e8f5e9;
  border: 1px solid #4caf50;
}

.brand-status-card.status-excellent .rank-number,
.brand-status-card.status-excellent .rank-context {
  color: #2e7d32;
}

.brand-status-card.status-excellent .status-label {
  color: #1b5e20;
}

.brand-status-card.status-good {
  background-color: #fff8e1;
  border: 1px solid #ffc107;
}

.brand-status-card.status-good .rank-number,
.brand-status-card.status-good .rank-context {
  color: #f57f17;
}

.brand-status-card.status-good .status-label {
  color: #e65100;
}

.brand-status-card.status-poor {
  background-color: #fff3e0;
  border: 1px solid #ff9800;
}

.brand-status-card.status-poor .rank-number,
.brand-status-card.status-poor .rank-context {
  color: #e65100;
}

.brand-status-card.status-poor .status-label {
  color: #bf360c;
}

.brand-status-card.status-not-mentioned {
  background-color: #ffebee;
  border: 1px solid #ef5350;
}

.brand-status-card.status-not-mentioned .rank-number,
.brand-status-card.status-not-mentioned .rank-context {
  color: #c62828;
}

.brand-status-card.status-not-mentioned .status-label {
  color: #b71c1c;
}

/* Competitors section */
.competitors-section {
  display: flex;
  flex-direction: column;
  min-width: 140px;
}

.competitors-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  transition: all 0.15s ease;
}

.competitors-toggle:hover {
  background: #eeeeee;
  border-color: #bdbdbd;
}

.toggle-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.competitors-list {
  margin-top: 8px;
  padding: 8px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.competitor-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
}

.competitor-item:not(:last-child) {
  border-bottom: 1px solid #eee;
  padding-bottom: 6px;
  margin-bottom: 2px;
}

.competitor-rank {
  font-weight: 700;
  color: #666;
  min-width: 24px;
}

.competitor-name {
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Primary brand highlight styles - matching badge colors */
.competitor-item.highlight-excellent {
  background-color: #e8f5e9;
  border-radius: 4px;
  padding: 6px 8px !important;
  margin: -2px -4px;
}

.competitor-item.highlight-excellent .primary-rank {
  color: #2e7d32;
  font-weight: 800;
}

.competitor-item.highlight-excellent .primary-name {
  color: #1b5e20;
  font-weight: 600;
}

.competitor-item.highlight-good {
  background-color: #fff8e1;
  border-radius: 4px;
  padding: 6px 8px !important;
  margin: -2px -4px;
}

.competitor-item.highlight-good .primary-rank {
  color: #f57f17;
  font-weight: 800;
}

.competitor-item.highlight-good .primary-name {
  color: #e65100;
  font-weight: 600;
}

.competitor-item.highlight-poor {
  background-color: #fff3e0;
  border-radius: 4px;
  padding: 6px 8px !important;
  margin: -2px -4px;
}

.competitor-item.highlight-poor .primary-rank {
  color: #e65100;
  font-weight: 800;
}

.competitor-item.highlight-poor .primary-name {
  color: #bf360c;
  font-weight: 600;
}

.competitor-item.highlight-not-mentioned {
  background-color: #ffebee;
  border-radius: 4px;
  padding: 6px 8px !important;
  margin: -2px -4px;
}

.competitor-item.highlight-not-mentioned .primary-rank {
  color: #c62828;
  font-weight: 800;
}

.competitor-item.highlight-not-mentioned .primary-name {
  color: #b71c1c;
  font-weight: 600;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .query-row {
    flex-direction: column;
  }
  
  .status-section {
    width: 100%;
    justify-content: flex-start;
    padding-left: 26px;
  }
}
</style>

